// ============================================================================
// OMS Testing — stand up real orders for the OMS test environment.
//
// Two sub-tabs, both backed by the flag-gated /oms/test-scenarios/ API:
//   • Quick Scenario — one main case (one item, one route), the fastest path.
//   • Detail Test    — a free-hand, multi-line estimate (a route per line,
//                      additional charges, a custom payment ledger, config).
//
// Everything here creates REAL data (Estimate + items, an EstimatePaymentTransaction
// plus a matching Zoho advance, an OMS SalesOrder that drives real Zoho documents).
// It is NOT a simulation, which is why the server keeps it behind a flag.
// ============================================================================
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ScenarioDocument,
  ScenarioResult,
  TestScenarioOptions,
  getTestScenarioOptions,
  runTestScenario,
} from "./api";
import OmsDetailTest from "./detail_test";
import OmsTestPaymentPanel from "./payment_panel";

const { Title, Text, Paragraph } = Typography;

const DOC_STATE_COLOUR: Record<string, string> = {
  created: "green",
  pending: "blue",
  submitted: "gold",
  failed: "red",
};

const STAGE_STATE_COLOUR: Record<string, string> = {
  completed: "green",
  issued: "blue",
  planned: "default",
  cancelled: "default",
  held: "orange",
  error: "red",
};

// --- Quick Scenario builder (one main case) ---------------------------------
const QuickScenarioPanel: React.FC<{ options: TestScenarioOptions }> = ({
  options,
}) => {
  const [running, setRunning] = useState(false);

  const [scenario, setScenario] = useState<string | undefined>(
    options.scenarios?.[0]?.key
  );
  const [deliveryKey, setDeliveryKey] = useState<string | undefined>(
    options.delivery_locations?.[0]?.key
  );
  const [variantId, setVariantId] = useState<number | undefined>(
    options.products?.find((p) => p.linked_to_zoho)?.id
  );
  const [qty, setQty] = useState<number>(3);

  const [result, setResult] = useState<ScenarioResult | undefined>();
  const [error, setError] = useState<string | undefined>();

  const selectedScenario = useMemo(
    () => options.scenarios?.find((s) => s.key === scenario),
    [options, scenario]
  );
  const selectedDelivery = useMemo(
    () => options.delivery_locations?.find((d) => d.key === deliveryKey),
    [options, deliveryKey]
  );

  const onRun = async () => {
    if (!scenario) return;
    setRunning(true);
    setError(undefined);
    setResult(undefined);
    try {
      const data = await runTestScenario({
        scenario,
        delivery_key: deliveryKey,
        variant_id: variantId,
        qty,
      });
      setResult(data);
      message.success(`Created SalesOrder ${data.sales_order_id}`);
    } catch (e: any) {
      const detail =
        e?.response?.data?.detail ??
        e?.message ??
        "Scenario failed for an unknown reason";
      setError(String(detail));
    } finally {
      setRunning(false);
    }
  };

  const docColumns = [
    { title: "Document", dataIndex: "type", key: "type" },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      render: (s: string) => <Tag color={DOC_STATE_COLOUR[s] ?? "default"}>{s}</Tag>,
    },
    {
      title: "Zoho id",
      dataIndex: "zoho_doc_id",
      key: "zoho_doc_id",
      render: (v: string | null) => v ?? "—",
    },
  ];

  return (
    <Row gutter={16}>
      <Col xs={24} lg={9}>
        <Card size="small" title="Build an order">
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <div>
              <Text strong>Scenario</Text>
              <Select
                style={{ width: "100%", marginTop: 4 }}
                value={scenario}
                onChange={setScenario}
                options={options.scenarios.map((s) => ({
                  value: s.key,
                  label: `${s.label}`,
                }))}
              />
              {selectedScenario ? (
                <Paragraph
                  type="secondary"
                  style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}
                >
                  <Tag>{selectedScenario.main_case}</Tag>
                  {selectedScenario.description}
                </Paragraph>
              ) : null}
            </div>

            <div>
              <Text strong>Deliver to</Text>
              <Select
                style={{ width: "100%", marginTop: 4 }}
                value={deliveryKey}
                onChange={setDeliveryKey}
                options={(options.delivery_locations ?? []).map((d) => ({
                  value: d.key,
                  label: d.label,
                }))}
              />
              {selectedDelivery ? (
                <Paragraph
                  type="secondary"
                  style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}
                >
                  <Tag color="purple">{selectedDelivery.main_case}</Tag>
                  {selectedDelivery.description}
                </Paragraph>
              ) : null}
            </div>

            <div>
              <Text strong>Product</Text>
              <Select
                style={{ width: "100%", marginTop: 4 }}
                value={variantId}
                onChange={setVariantId}
                showSearch
                optionFilterProp="label"
                options={options.products.map((p) => ({
                  value: p.id,
                  label: `${p.sku}${p.linked_to_zoho ? "" : "  (new in Zoho)"}`,
                }))}
              />
            </div>

            <div>
              <Text strong>Quantity</Text>
              <InputNumber
                style={{ width: "100%", marginTop: 4 }}
                min={1}
                value={qty}
                onChange={(v) => setQty(Number(v) || 1)}
              />
            </div>

            <Text type="secondary" style={{ fontSize: 12 }}>
              Creates the estimate, its payment and the sales order, then stops — the
              order advances only through the normal OMS paths, so drive it from the
              OMS screens or with real webhooks.
            </Text>

            <Button
              type="primary"
              block
              loading={running}
              onClick={onRun}
              disabled={!scenario}
            >
              {running ? "Building order…" : "Create order"}
            </Button>
          </Space>
        </Card>
      </Col>

      <Col xs={24} lg={15}>
        {error ? (
          <Alert
            type="error"
            showIcon
            message="Scenario failed"
            description={
              <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{error}</pre>
            }
            style={{ marginBottom: 16 }}
          />
        ) : null}

        {result ? (
          <Card
            size="small"
            title={`SalesOrder ${result.sales_order_id}`}
            extra={<Link to={`/oms/${result.sales_order_id}`}>Open in OMS</Link>}
          >
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Estimate">
                {result.estimate_id}
              </Descriptions.Item>
              <Descriptions.Item label="Ship to">
                {result.ship_pincode ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color="blue">{result.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Mode">{result.mode}</Descriptions.Item>
              <Descriptions.Item label="Terms">
                {result.payment_terms}
              </Descriptions.Item>
              <Descriptions.Item label="Paid">
                ₹{result.payment.amount_rupees}
              </Descriptions.Item>
              <Descriptions.Item label="Zoho credit">
                {result.payment.zoho_payment_id ?? "— (deferred)"}
              </Descriptions.Item>
            </Descriptions>

            {result.notes?.length ? (
              <Alert
                type="info"
                style={{ marginTop: 12 }}
                message={result.notes.join(" · ")}
              />
            ) : null}

            <Divider orientation="left" style={{ marginTop: 16 }}>
              Allocations
            </Divider>
            {result.allocations.map((a) => (
              <div key={a.id} style={{ marginBottom: 12 }}>
                <Space wrap>
                  <Text strong>#{a.id}</Text>
                  <Tag>{a.shipment_type}</Tag>
                  {a.stages.map((s) => (
                    <Tag
                      key={s.id}
                      color={STAGE_STATE_COLOUR[s.state] ?? "default"}
                      title={s.error ?? s.hold_reason ?? undefined}
                    >
                      {s.type}: {s.state}
                    </Tag>
                  ))}
                </Space>
                {a.stages
                  .filter((s) => s.error || s.hold_reason)
                  .map((s) => (
                    <Alert
                      key={`err-${s.id}`}
                      type={s.error ? "error" : "warning"}
                      style={{ marginTop: 6 }}
                      message={`${s.type}: ${s.error ?? s.hold_reason}`}
                    />
                  ))}
              </div>
            ))}

            <Divider orientation="left">Documents</Divider>
            <Table<ScenarioDocument>
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={result.documents}
              columns={docColumns}
            />
          </Card>
        ) : !error ? (
          <Card size="small">
            <Text type="secondary">
              Pick a scenario and create an order — the resulting sales order,
              allocations and Zoho documents show up here.
            </Text>
          </Card>
        ) : null}
      </Col>
    </Row>
  );
};

// --- Container: loads options once, hosts the sub-tabs + shared payment panel
const OmsTesting: React.FC = () => {
  const [options, setOptions] = useState<TestScenarioOptions | undefined>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const data = await getTestScenarioOptions();
      setOptions(data);
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!options?.enabled) {
    return (
      <div style={{ padding: 24 }}>
        <Title level={4}>OMS Testing</Title>
        <Alert
          type="info"
          showIcon
          message="Disabled in this environment"
          description="The OMS scenario harness creates real orders, payments and Zoho documents, so it is only enabled on the OMS test environment (OMS_TEST_SCENARIOS_ENABLED)."
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginBottom: 4 }}>
        OMS Testing
      </Title>
      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Builds <strong>real</strong> orders — an estimate and its items, the customer
        payment (ledger entry plus a Zoho advance credit), then the OMS sales order
        that takes over fulfilment.
        {options.outbox_backend ? (
          <>
            {" "}
            Outbox backend: <Tag>{options.outbox_backend}</Tag>
          </>
        ) : null}
      </Paragraph>

      <Tabs
        defaultActiveKey="quick"
        items={[
          {
            key: "quick",
            label: "Quick Scenario",
            children: <QuickScenarioPanel options={options} />,
          },
          {
            key: "detail",
            label: "Detail Test",
            children: <OmsDetailTest options={options} />,
          },
        ]}
      />

      <OmsTestPaymentPanel />
    </div>
  );
};

export default OmsTesting;
